import { DayMeta, DaySession, DayStatus, OptionKey, Question } from '../data/types';
import { DAYS_META, getDayMeta, getQuestionsForDay } from '../data/questionsData';

/**
 * Lấy ID của người dùng đang đăng nhập để cách ly hoàn toàn dữ liệu bài thi giữa các tài khoản khác nhau
 */
export function getCurrentUserId(): string {
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.id) return String(u.id);
      if (u?.user_id) return String(u.user_id);
      if (u?.email) return String(u.email);
    }
  } catch {}
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  if (token) {
    try {
      const parts = token.split('.');
      if (parts.length >= 2) {
        const payload = JSON.parse(atob(parts[1]));
        if (payload?.id) return String(payload.id);
        if (payload?.user_id) return String(payload.user_id);
      }
    } catch {}
    return token.slice(-12);
  }
  return 'guest';
}

export function getQuizStorageKey(): string {
  const uid = getCurrentUserId();
  return `fc_quiz_user_${uid}_state_v1`;
}

interface StoredDayData {
  status: DayStatus;
  answers: Record<number, OptionKey>;
  flagged: number[];
  currentIndex: number;
  startedAt: string | null;
  submittedAt: string | null;
  lastSavedAt: string | null;
}

interface StoredState {
  days: Record<number, StoredDayData>;
}

function getInitialState(): StoredState {
  return {
    days: {
      1: {
        status: 'AVAILABLE',
        answers: {},
        flagged: [],
        currentIndex: 0,
        startedAt: null,
        submittedAt: null,
        lastSavedAt: null,
      },
      2: {
        status: 'AVAILABLE',
        answers: {},
        flagged: [],
        currentIndex: 0,
        startedAt: null,
        submittedAt: null,
        lastSavedAt: null,
      },
      3: {
        status: 'AVAILABLE',
        answers: {},
        flagged: [],
        currentIndex: 0,
        startedAt: null,
        submittedAt: null,
        lastSavedAt: null,
      },
    },
  };
}

class QuizService {
  private state: StoredState;
  private currentUserId: string;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.currentUserId = getCurrentUserId();
    // Xóa sạch key cũ dùng chung để không bao giờ bị dính đáp án của tài khoản khác
    this.cleanLegacyStorage();
    this.state = this.loadState();
    this.refreshLockedStates();
  }

  private cleanLegacyStorage(): void {
    try {
      localStorage.removeItem('fc_quiz_student_state_v1');
      for (let d = 1; d <= 20; d++) {
        localStorage.removeItem(`fc_quiz_locked_day_${d}`);
      }
    } catch {}
  }

  /**
   * Tự động đồng bộ và nạp lại state khi chuyển sang tài khoản người dùng khác
   */
  public syncUser(): void {
    const newUid = getCurrentUserId();
    if (this.currentUserId !== newUid) {
      this.currentUserId = newUid;
      this.state = this.loadState();
      this.refreshLockedStates();
      this.notify();
    }
  }

  private loadState(): StoredState {
    try {
      const key = getQuizStorageKey();
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.days) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Failed to load quiz state from localStorage', e);
    }
    return getInitialState();
  }

  private saveState(): void {
    try {
      const key = getQuizStorageKey();
      localStorage.setItem(key, JSON.stringify(this.state));
    } catch (e) {
      console.error('Failed to save quiz state to localStorage', e);
    }
    this.notify();
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        console.error('QuizService listener error', e);
      }
    });
  }

  private refreshLockedStates(): void {
    // Unlock any previously locked day so student can navigate freely
    Object.keys(this.state.days).forEach((key) => {
      const day = Number(key);
      if (this.state.days[day]?.status === 'LOCKED') {
        this.state.days[day].status = 'AVAILABLE';
      }
    });
  }

  public getDayMeta(day: number): DayMeta | undefined {
    return getDayMeta(day);
  }

  public getAllDaysMeta(): DayMeta[] {
    return DAYS_META;
  }

  public getDayStatus(day: number): DayStatus {
    this.refreshLockedStates();
    const d = this.state.days[day];
    return d ? d.status : 'AVAILABLE';
  }

  public getAllDayStatuses(): Record<number, DayStatus> {
    this.refreshLockedStates();
    const result: Record<number, DayStatus> = {};
    Object.keys(this.state.days).forEach((key) => {
      const day = Number(key);
      result[day] = this.getDayStatus(day);
    });
    return result;
  }

  public getDaySession(day: number): DaySession {
    this.refreshLockedStates();
    let dayData = this.state.days[day];
    if (!dayData) {
      dayData = {
        status: 'AVAILABLE',
        answers: {},
        flagged: [],
        currentIndex: 0,
        startedAt: null,
        submittedAt: null,
        lastSavedAt: null,
      };
      this.state.days[day] = dayData;
    }

    const questions = getQuestionsForDay(day);

    return {
      day,
      status: dayData.status,
      locked: dayData.status === 'COMPLETED',
      questions,
      answers: { ...dayData.answers },
      flagged: [...dayData.flagged],
      currentIndex: Math.max(0, Math.min(questions.length - 1, dayData.currentIndex || 0)),
      startedAt: dayData.startedAt,
      lastSavedAt: dayData.lastSavedAt,
      submittedAt: dayData.submittedAt,
    };
  }

  public startDay(day: number): DaySession {
    this.refreshLockedStates();
    let dayData = this.state.days[day];
    if (!dayData) {
      dayData = {
        status: 'IN_PROGRESS',
        answers: {},
        flagged: [],
        currentIndex: 0,
        startedAt: new Date().toISOString(),
        submittedAt: null,
        lastSavedAt: null,
      };
      this.state.days[day] = dayData;
    }

    if (dayData.status !== 'COMPLETED') {
      dayData.status = 'IN_PROGRESS';
      if (!dayData.startedAt) {
        dayData.startedAt = new Date().toISOString();
      }
      this.saveState();
    }

    return this.getDaySession(day);
  }

  public syncDayData(
    day: number,
    data: {
      status?: DayStatus;
      answers?: Record<number, OptionKey>;
      submittedAt?: string | null;
      currentIndex?: number;
      replaceAnswers?: boolean;
    }
  ): void {
    let dayData = this.state.days[day];
    if (!dayData) {
      dayData = {
        status: data.status || 'AVAILABLE',
        answers: data.answers ? { ...data.answers } : {},
        flagged: [],
        currentIndex: data.currentIndex || 0,
        startedAt: null,
        submittedAt: data.submittedAt || null,
        lastSavedAt: null,
      };
      this.state.days[day] = dayData;
    } else {
      if (data.status) dayData.status = data.status;
      if (data.answers) {
        if (data.replaceAnswers || data.status === 'COMPLETED') {
          dayData.answers = { ...data.answers };
        } else {
          dayData.answers = { ...dayData.answers, ...data.answers };
        }
      }
      if (data.submittedAt !== undefined) dayData.submittedAt = data.submittedAt;
      if (data.currentIndex !== undefined) dayData.currentIndex = data.currentIndex;
    }
    this.saveState();
  }

  public saveAnswer(day: number, questionId: number, optionKey: OptionKey): void {
    let dayData = this.state.days[day];
    if (!dayData) {
      dayData = {
        status: 'IN_PROGRESS',
        answers: {},
        flagged: [],
        currentIndex: 0,
        startedAt: new Date().toISOString(),
        submittedAt: null,
        lastSavedAt: null,
      };
      this.state.days[day] = dayData;
    }

    if (dayData.status === 'COMPLETED') {
      return;
    }

    // Auto-transition to IN_PROGRESS when answering
    if (dayData.status === 'AVAILABLE' || dayData.status === 'LOCKED') {
      dayData.status = 'IN_PROGRESS';
      if (!dayData.startedAt) {
        dayData.startedAt = new Date().toISOString();
      }
    }

    dayData.answers[questionId] = optionKey;
    dayData.lastSavedAt = new Date().toISOString();
    this.saveState();
  }

  public removeAnswer(day: number, questionId: number): void {
    const dayData = this.state.days[day];
    if (dayData && dayData.answers) {
      delete dayData.answers[questionId];
      dayData.lastSavedAt = new Date().toISOString();
      this.saveState();
    }
  }

  public saveAnswers(day: number, answers: Record<number, OptionKey>): void {
    let dayData = this.state.days[day];
    if (!dayData) {
      dayData = {
        status: 'IN_PROGRESS',
        answers: { ...answers },
        flagged: [],
        currentIndex: 0,
        startedAt: new Date().toISOString(),
        submittedAt: null,
        lastSavedAt: new Date().toISOString(),
      };
      this.state.days[day] = dayData;
    } else {
      dayData.answers = { ...answers };
      dayData.lastSavedAt = new Date().toISOString();
    }
    this.saveState();
  }

  public toggleFlag(day: number, questionId: number): void {
    let dayData = this.state.days[day];
    if (!dayData || dayData.status === 'COMPLETED') return;

    if (dayData.status === 'AVAILABLE' || dayData.status === 'LOCKED') {
      dayData.status = 'IN_PROGRESS';
      if (!dayData.startedAt) {
        dayData.startedAt = new Date().toISOString();
      }
    }

    const idx = dayData.flagged.indexOf(questionId);
    if (idx >= 0) {
      dayData.flagged.splice(idx, 1);
    } else {
      dayData.flagged.push(questionId);
    }
    this.saveState();
  }

  public setCurrentIndex(day: number, index: number): void {
    let dayData = this.state.days[day];
    if (!dayData) {
      dayData = {
        status: 'AVAILABLE',
        answers: {},
        flagged: [],
        currentIndex: index,
        startedAt: null,
        submittedAt: null,
        lastSavedAt: null,
      };
      this.state.days[day] = dayData;
      this.saveState();
      return;
    }
    if (dayData.currentIndex !== index) {
      dayData.currentIndex = index;
      this.saveState();
    }
  }

  public submitDay(day: number): void {
    const dayData = this.state.days[day];
    if (!dayData) {
      throw new Error(`Ngày ${day} không tồn tại.`);
    }

    const questions = getQuestionsForDay(day);
    const unanswered = questions.filter((q) => !dayData.answers[q.id]);
    if (unanswered.length > 0) {
      throw new Error(`Còn ${unanswered.length} câu chưa trả lời.`);
    }

    dayData.status = 'COMPLETED';
    dayData.submittedAt = new Date().toISOString();

    // Ensure next day is available
    if (this.state.days[day + 1]) {
      if (this.state.days[day + 1].status === 'LOCKED') {
        this.state.days[day + 1].status = 'AVAILABLE';
      }
    }

    this.saveState();
  }

  public resetAllDemo(): void {
    this.state = getInitialState();
    this.saveState();
  }
}

export const quizService = new QuizService();
